import { ClientSafeLaunchProofViewV1Schema, } from "../contracts/launch-proof-views.js";
import { buildLaunchProofReport, } from "./build-launch-proof-report.js";
import { clientSafeProofReferenceLabel, isInternalProofReferenceRef } from "./client-safe-proof-reference-label.js";
import { assertNoClientSafeMetadataLeaks, assertNoForbiddenClientSafePhrases, } from "./sanitize-client-safe-report.js";
/** v1.1 hard-gate forbidden keys/phrases for Launch Proof client-safe output. */
export const LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_KEYS = [
    "runId",
    "generationRunId",
    "artifactId",
    "artifactSetHash",
    "exportBundleHash",
    "checkId",
    "internalDetail",
    "agencyId",
    "projectId",
    "businessGraphRevisionId",
    "snapshotHash",
    "qualityGateResultId",
    "qualityGateResultRef",
];
export const LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_PHRASES = [
    "generation api",
    "surface engine",
    "stack trace",
    "stacktrace",
    "debug text",
    "worker internals",
    "at Object.",
    "at Module.",
];
export const LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_SCHEMA_VERSIONS = [
    "launch-proof-report.v1.0",
    "phase1-check-result.v1.0",
];
export function assertLaunchProofClientSafeHardGate(json) {
    for (const key of LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_KEYS) {
        if (json.includes(`"${key}"`)) {
            throw new Error(`client_safe_launch_proof_unsafe:${key}`);
        }
    }
    const lower = json.toLowerCase();
    for (const phrase of LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_PHRASES) {
        if (lower.includes(phrase)) {
            throw new Error(`client_safe_launch_proof_unsafe_phrase:${phrase}`);
        }
    }
    for (const schemaVersion of LAUNCH_PROOF_CLIENT_SAFE_FORBIDDEN_SCHEMA_VERSIONS) {
        if (json.includes(`"schemaVersion":"${schemaVersion}"`) ||
            json.includes(`"schemaVersion": "${schemaVersion}"`)) {
            throw new Error(`client_safe_launch_proof_unsafe_schema:${schemaVersion}`);
        }
    }
}
const FORBIDDEN_PASS_PHRASES_WITHOUT_EXPLICIT_PASS = [
    "checks passed",
    "all checks passed",
    "fully ready",
    "launch certified",
    "certificate",
];
function aggregateStatusLabel(status) {
    switch (status) {
        case "ready":
            return "Evaluated checks passed at this reference";
        case "ready_with_warnings":
            return "Completed with warnings";
        case "blocked":
            return "Blocking issues noted";
        case "not_evaluated":
            return "Not evaluated";
        default:
            return "Not evaluated";
    }
}
function phase1StatusLabel(status) {
    switch (status) {
        case "pass":
            return "Passed";
        case "warn":
            return "Completed with warnings";
        case "fail":
            return "Needs attention";
        case "not_evaluated":
            return "Not evaluated";
        default:
            return "Not evaluated";
    }
}
function toClientSafePhase1Section(section) {
    return {
        schemaVersion: "client-safe-launch-proof-phase1-section.v1.0",
        areaLabel: section.areaLabel,
        statusLabel: phase1StatusLabel(section.status),
        summary: section.clientSafeSummary,
    };
}
function assertNoUnauthorizedPassLanguage(report) {
    const hasExplicitPass = report.phase1Sections.some((s) => s.status === "pass");
    if (hasExplicitPass) {
        return;
    }
    const scopedBlob = JSON.stringify({
        headline: report.headline,
        aggregateStatusLabel: aggregateStatusLabel(report.aggregateStatus),
        phase1Sections: report.phase1Sections.map(toClientSafePhase1Section),
    }).toLowerCase();
    for (const phrase of FORBIDDEN_PASS_PHRASES_WITHOUT_EXPLICIT_PASS) {
        if (scopedBlob.includes(phrase)) {
            throw new Error(`client_safe_unauthorized_pass_phrase:${phrase}`);
        }
    }
}
export function toClientSafeLaunchProofView(report) {
    const view = ClientSafeLaunchProofViewV1Schema.parse({
        schemaVersion: "client-safe-launch-proof-view.v1.0",
        proofReferenceLabel: clientSafeProofReferenceLabel({
            rawLabel: report.proofReferenceLabel,
            headline: report.headline,
        }),
        aggregateStatusLabel: aggregateStatusLabel(report.aggregateStatus),
        headline: report.headline,
        phase1Sections: report.phase1Sections.map(toClientSafePhase1Section),
        searchReadinessExcerpt: report.searchReadinessExcerpt
            ? {
                schemaVersion: "client-safe-launch-proof-search-excerpt.v1.0",
                headline: report.searchReadinessExcerpt.headline,
                coverageSummary: `${report.searchReadinessExcerpt.coverage.coveragePercent}% coverage (${report.searchReadinessExcerpt.coverage.checksPassed} of ${report.searchReadinessExcerpt.coverage.checksTotal} areas reviewed)`,
                staleBanner: report.searchReadinessExcerpt.staleBanner,
            }
            : undefined,
        departureKitSummary: report.departureKitSummary,
        whatWeDoNotPromise: [...report.whatWeDoNotPromise],
        launchProofDisclaimers: [...report.launchProofDisclaimers],
        generatedAt: report.generatedAt,
        clientSafe: true,
    });
    const blob = JSON.stringify(view);
    if (isInternalProofReferenceRef(view.proofReferenceLabel)) {
        throw new Error("client_safe_launch_proof_internal_ref_leak");
    }
    assertNoForbiddenClientSafePhrases(blob);
    assertNoClientSafeMetadataLeaks(blob);
    assertLaunchProofClientSafeHardGate(blob);
    assertNoUnauthorizedPassLanguage(report);
    return view;
}
export function buildClientSafeLaunchProofView(input) {
    return toClientSafeLaunchProofView(buildLaunchProofReport(input));
}
//# sourceMappingURL=sanitize-client-safe-launch-proof.js.map